/**
 * Store for the REST APIs.
 *
 * File: \src\api\ApiStore.ts
 * Created Date: Thursday, April 28th 2022, 09:07:38
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: November 25th 2024, 09:41:08
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2022 Liebherr-Hausgeräte GmbH
 */
import {
  AlarmControllerApi,
  ApplianceControllerApi,
  BsdbControllerApi,
  ConfigControllerApi,
  LogControllerApi,
  MessagingControllerApi,
  OrganizationControllerApi,
  SupportControllerApi,
  UserControllerApi
} from '@/api/apis';

import { Configuration, ConfigurationParameters, FetchParams, Middleware, RequestContext } from '@/api/runtime';

import EnvironmentHandler from '@/EnvironmentHandler';
import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { useLoginStore } from '@/components/login/LoginStore';
import { CubeLogger } from '@/Logger';

/**
 * This middleware adds the timezone name to every request.
 * Timezone is determined using luxon.
 *
 * @export
 * @class TimeZoneMiddleware
 * @implements {Middleware}
 */
class BusMiddleware implements Middleware {
  public context: string = undefined;
  public country: string = undefined;
  public company: string = 'liebherr';

  /**
   * Sets the context. That is sent on every request. You can only set this once.
   *
   * @param {string} context The new context.
   * @memberof BusMiddleware
   */
  public setContext(context: string): void {
    if (context && !this.context) {
      this.context = context;
    }
  }

  /**
   * Sets the country. This is sent on every request. You can only set this once.
   *
   * @param {string} country The new country code.
   * @memberof BusMiddleware
   */
  public setCountry(country: string): void {
    if (country && !this.country) {
      this.country = country;
    }
  }

  /**
   * Sets the company. This can be set always when the user wants to change its main company. (e.g. 'wepa', 'liebherr')
   *
   * @param {string} company
   * @memberof BusMiddleware
   */
  public setCompany(company: string): void {
    this.company = company;
  }

  /**
   * Called before the https call. Used to add "Timezone", "Context", "Country" and "Company" to the headers.
   *
   * @param {RequestArgs} requestContext The request context.
   *
   * @returns {Promise<FetchParams | void>} the modified params for the request.
   *
   * @memberof BusMiddleware
   */
  public async pre(requestContext: RequestContext): Promise<FetchParams | void> {
    const url: string = requestContext.url;
    const init: RequestInit = requestContext.init;
    const headers: HeadersInit = init.headers;

    // Construct a new headers object with the values of the old one.
    const initHeaders: Headers = new Headers(headers);

    let zoneName: string = DateTime.local().zoneName;
    initHeaders.append('Timezone', zoneName);

    if (this.context) {
      initHeaders.append('Context', this.context);
    }

    if (this.country) {
      initHeaders.append('Country', this.country);
    }

    if (this.company) {
      initHeaders.append('Company', this.company);
    }

    init.headers = initHeaders;

    return { url, init };
  }
}

/**
 * Interface for the storage of the APIs plus authentication.
 *
 * @export
 * @interface ApiStoreState
 */
interface ApiStoreState {
  busMiddleWare: BusMiddleware;
}

/**
 * Store for APIs. All classes use this to get access to backend methods.
 *
 * @export
 * @class ApiStore
 * @extends {VueAppStoreModule<ApiState, RootState>}
 */
export const useApiStore = defineStore('apiStore', {
  /**
   * The initial state.
   *
   */
  state: (): ApiStoreState => ({
    busMiddleWare: new BusMiddleware()
  }),

  getters: {
    /**
     * Getter for the AlarmControllerApi.
     *
     * @returns {AlarmControllerApi}
     */
    getAlarmControllerApi(): AlarmControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getAlarmControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: AlarmControllerApi = new AlarmControllerApi(configuration);

      return api;
    },

    /**
     * Gets the ApplianceControllerApi REST access class.
     *
     * @returns {ApplianceControllerApi}
     */
    getApplianceControllerApi(): ApplianceControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getApplianceControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: ApplianceControllerApi = new ApplianceControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the BSDB controller API used by the onboarding.
     *
     * @returns {BsdbControllerApi}
     */
    getBsdbControllerApi(): BsdbControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getBsdbControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: BsdbControllerApi = new BsdbControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the config controller API.
     *
     * @returns {ConfigControllerApi}
     */
    getConfigControllerApi(): ConfigControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getConfigControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: ConfigControllerApi = new ConfigControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the log controller API.
     * This method cannot log! It is used during the upload of logging!
     *
     * @returns {LogControllerApi}
     */
    getLogControllerApi(): LogControllerApi {
      const configuration: Configuration = this.getConfiguration;
      const api: LogControllerApi = new LogControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the messaging controller.
     *
     * @returns {MessagingControllerApi}
     */
    getMessagingControllerApi(): MessagingControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getMessagingControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: MessagingControllerApi = new MessagingControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the organization controller api used for the image upload.
     *
     * @returns {OrganizationControllerApi}
     */
    getOrganizationControllerApi(): OrganizationControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::OrganizationControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: OrganizationControllerApi = new OrganizationControllerApi(configuration);

      return api;
    },

    /**
     *  Gets the SupportControllerApi REST access class.
     *
     * @returns {SupportControllerApi}
     */
    getSupportControllerApi(): SupportControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getSupportControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: SupportControllerApi = new SupportControllerApi(configuration);

      return api;
    },

    /**
     * Getter for the user controller API used by the application.
     *
     * @returns {UserControllerApi}
     */
    getUserControllerApi(): UserControllerApi {
      const logger = CubeLogger.$log.prefix('ApiStore::getUserControllerApi');
      logger.debug('Getting API ...');

      const configuration: Configuration = this.getConfiguration;
      const api: UserControllerApi = new UserControllerApi(configuration);

      return api;
    },

    /**
     * Gets the authorization for the call.
     *
     * @returns {Configuration} The OAuth object with the access token coming from the loginStore.
     */
    getConfiguration(): Configuration {
      const configurationParams: ConfigurationParameters = this.getConfigurationParameters();
      const configuration: Configuration = new Configuration(configurationParams);

      return configuration;
    },

    /**
     * Separate getter for the configuration parameters. This allows to add or modify them.
     *
     * @returns {ConfigurationParameters}
     */
    getConfigurationParameters(rootGetters): () => ConfigurationParameters {
      return (): ConfigurationParameters => {
        const loginStore = useLoginStore();
        const accessToken: string = loginStore.getBearerToken();

        // Here adding middlewares to the config (and thereby to the API) works
        // Adding it using with withPreMiddleware() did not.

        // Add the Timezone and context middleware.
        const middlewares: Middleware[] = [this.busMiddleWare];

        const configurationParams: ConfigurationParameters = {
          apiKey: 'Bearer ' + accessToken,
          basePath: EnvironmentHandler.REST_URL,
          middleware: middlewares
        };

        return configurationParams;
      };
    }
  },

  actions: {
    /**
     * Set a user context object. Just calls the mutation. The context is encrypted information.
     *
     * @param {string} context The context to store.
     */
    setContext(context: string): void {
      this.storeContext(context);
    },

    /**
     * Sets the country for the calls to the backend.
     *
     * @param {string} country The country code (e.g. "GB").
     */
    setCountry(country: string): void {
      this.storeCountry(country);
    },

    /**
     * Sets the company for the calls to the backend.
     *
     * @param {string} company The company (e.g. 'wepa', 'liebherr')
     */
    setCompany(company: string): void {
      this.storeCompany(company);
    },

    /**
     * Stores the context in the middleware. Can be only done once.
     *
     * @param {string} context The context to store.
     */
    storeContext(context: string): void {
      this.busMiddleWare.setContext(context);
    },

    /**
     * Stores the context in the middleware. Can be only done once.
     *
     * @param {string} country The country to store.
     */
    storeCountry(country: string): void {
      this.busMiddleWare.setCountry(country);
    },

    /**
     * Stores the company in the middleware. Can be set always when the suer wants to change company.
     *
     * @param {string} company The company to store, can be 'wepa' or 'liebherr'.
     */
    storeCompany(company: string): void {
      this.busMiddleWare.setCompany(company);
    }
  }
});
